import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Shell = ({ title, subtitle, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-[var(--bone)] px-6 py-12">
    <div className="w-full max-w-md">
      <Link to="/" className="mb-8 flex items-center justify-center">
        <img src="/customize-logo.jpg" alt="logo" className="h-80 w-auto rounded-2xl object-contain" />
      </Link>
      <div className="rounded-2xl border border-[var(--line)] bg-white p-8 shadow-sm">
        <h1 className="font-display text-2xl tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-[var(--ink-soft)]">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  </div>
);

export const Field = ({ label, ...props }) => (
  <label className="mb-4 block">
    <span className="overline">{label}</span>
    <input
      {...props}
      className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20"
    />
  </label>
);

export const PasswordField = ({ label, value, onChange, testid, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <label className="mb-4 block">
      <span className="overline">{label}</span>
      <div className="relative mt-1">
        <input
          {...props}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          data-testid={testid}
          className="w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 pr-11 text-sm outline-none transition-colors duration-200 focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20"
        />
        <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1}
          data-testid={testid ? `${testid}-toggle` : undefined}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)] transition-colors hover:text-[var(--ink)]">
          {show ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
        </button>
      </div>
    </label>
  );
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { applyAuth } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyAuth(data);
      navigate("/studio");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Welcome back" subtitle="Log in to your Customize Studio account">
      <form onSubmit={submit}>
        <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        <PasswordField label="Password" required value={password} onChange={(e) => setPassword(e.target.value)} testid="login-password" />
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="mt-2 w-full rounded-full bg-[var(--brand)] py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60"
        >
          {loading ? "Logging in…" : "Log in"}
        </button>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to="/forgot-password" data-testid="forgot-password-link" className="font-semibold text-[var(--brand)]">Forgot your password?</Link>
      </p>
      <p className="mt-2 text-center text-sm text-[var(--ink-soft)]">
        New here? <Link to="/signup" className="font-semibold text-[var(--brand)]">Create a company account</Link>
      </p>
    </Shell>
  );
}

export { Shell };
